import * as React from "react";
import type { EmailOtpType, Session } from "@supabase/supabase-js";
import type {
  Capability,
  CreateWorkspaceInput,
  CreateWorkspaceResponse,
  OAuthProvider,
  Scope,
  StartSignUpInput,
  UserWithContext,
} from "@taskiq/shared";

/**
 * The auth context and its hook, split from `auth.tsx` for the same reason
 * `confirm-context.ts` is split from `Confirm.tsx` (console-ui.md, rule 7):
 * React Fast Refresh treats a module that exports a component *and* anything
 * else as a non-boundary and re-evaluates it on edit, minting a fresh context
 * object while already-mounted consumers keep the old one. The symptom is
 * `useAuth` throwing "must be used inside <AuthProvider>" on a tree that
 * plainly has one, during dev, after an unrelated hot reload - and a full
 * reload making it vanish. Keeping `createContext` in a module that exports
 * no component is what makes the identity survive a refresh.
 *
 * `AuthProvider` stays in `auth.tsx` and is that file's only runtime export.
 */

/**
 * Session state for the console.
 *
 * Two sources, combined into one context:
 *   Supabase  owns the credential - who is signed in, and the access token.
 *   The API   owns the meaning - which company/site, which role, what they may do.
 *
 * Keeping them separate matters, and it is what the statuses below are for.
 * A user can hold a perfectly valid Supabase session and have no TaskIQ access
 * at all - because they just signed themselves up and have not created a
 * workspace, or because their account was deactivated. Neither is an error and
 * neither is "signed out"; each gets its own screen.
 */
export type AuthStatus =
  | "loading"
  /** No Supabase session. */
  | "signed-out"
  /**
   * Signed in, but no profile yet: a self sign-up between creating an identity
   * and creating a workspace. **The happy path, not a fault.** Routed to
   * `/onboarding`, which is the only screen that can resolve it.
   */
  | "onboarding"
  /** Signed in, profile exists, but deactivated. */
  | "pending"
  /**
   * Signed in, but `/auth/me` failed for a reason that says nothing about the
   * account: the API was unreachable, or returned an unexpected error. Kept out
   * of `pending` on purpose - telling a user their account is deactivated
   * because of a network blip is simply wrong, and worse when the account in
   * question is the very first superadmin with no administrator to ask.
   */
  | "error"
  /**
   * Signed in with a password, and the account has a verified second factor
   * that has not been presented yet. A real session, at `aal1`.
   *
   * Its own state rather than a flag on `ready`, for the same reason `pending`
   * is: it needs a different screen, and anything that treats it as signed-out
   * bounces a user who has already proved their password back to the form.
   */
  | "mfa"
  /** Signed in and provisioned. */
  | "ready";

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: UserWithContext | null;
  scope: Scope | null;
  capabilities: readonly Capability[];
  /** Why the account is pending, straight from the API. */
  pendingReason: string | null;
  /** Non-auth failure loading the profile (API down, etc.). */
  error: string | null;
}

export interface AuthApi extends AuthState {
  can: (capability: Capability) => boolean;
  /**
   * True when this identity signs in with a password and therefore needs to
   * choose one during onboarding. False for social sign-in, where the provider
   * owns the credential and a password screen would be a step that buys nothing.
   */
  needsPassword: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  /** Sign-up step one: create the identity and email a code. No password yet. */
  startSignUp: (input: StartSignUpInput, captchaToken?: string) => Promise<void>;
  /** Re-send the code for an address already part-way through step two. */
  resendEmailCode: (email: string, captchaToken?: string) => Promise<void>;
  /** Sign-up step two: exchange the code for a real session. */
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  signInWithProvider: (provider: OAuthProvider) => Promise<void>;
  createWorkspace: (input: CreateWorkspaceInput) => Promise<CreateWorkspaceResponse>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string, captchaToken?: string) => Promise<void>;
  /**
   * Redeem an emailed invite or recovery token and set the password in one
   * action, so opening the link never consumes it. See the implementation.
   */
  redeemAndSetPassword: (
    tokenHash: string,
    type: Extract<EmailOtpType, "invite" | "recovery">,
    password: string
  ) => Promise<void>;
  /**
   * Change the password of an account that already has a live session.
   *
   * `currentPassword` is only ever sent by the account page's own dialog - the
   * onboarding and reset flows call this to set a *first* password, where there
   * is nothing yet to confirm. GoTrue enforces the requirement itself
   * (`GOTRUE_SECURITY_UPDATE_PASSWORD_REQUIRE_CURRENT_PASSWORD`), so omitting it
   * where an account already has a password fails the request rather than
   * silently skipping the check.
   */
  updatePassword: (password: string, currentPassword?: string) => Promise<void>;

  /**
   * Present the second factor for a session sitting at `aal1`.
   *
   * `challengeAndVerify` rather than `challenge` then `verify`: the two are
   * always called together, a challenge is useless on its own, and holding a
   * challenge id in component state is one more thing to lose on a re-render
   * halfway through somebody typing six digits.
   */
  verifyMfa: (code: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = React.createContext<AuthApi | null>(null);

export function useAuth(): AuthApi {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}
